import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormArray, FormsModule, AbstractControl } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputNumberComponent } from '../../../components/input-number/input-number.component';
import { EmployeeLevel, Role } from '../types';

export type EmployeeLocation = Record<string, unknown>;

export type EmployeeType = Record<string, unknown>;

@Component({
    selector: 'cost-plan-form-item',
    imports: [CommonModule, FormsModule, ReactiveFormsModule, AccordionModule, CheckboxModule, SelectModule, InputNumberModule, InputNumberComponent],
    templateUrl: './cost-plan-form-item.component.html',
    styleUrl: './cost-plan-form-item.component.less',
})
export class CostPlanFormItemComponent implements OnInit {
    @Input() role: Role | null = null;
    @Input() employeeLocations: EmployeeLocation[] = [];
    @Input() employeeLevels: EmployeeLevel[] = [];
    @Input() employeeTypes: EmployeeType[] = [];

    @Output() formChange = new EventEmitter<Record<string, unknown>>();

    fb: FormBuilder = new FormBuilder();

    roleForm = this.fb.group({
        roleName: this.role?.['roleName'],
        list: this.fb.array([]),
    });

    get list(): FormArray {
        return this.roleForm.get('list') as FormArray;
    }

    isSelectedAll: boolean | undefined = false;

    employeeAmonuts: number = 0;

    ngOnInit(): void {
        this.roleForm.patchValue({
            roleName: this.role?.['roleName'],
        });
        this.role?.['list'].map((item: any) => {
            const defaultLevel: number | null = item.level || this.employeeLevels[0]?.['id'] || null;
            const defaultType: number | null = item.type || this.employeeTypes[0]?.['id'] || null;
            const defaultLocation: number | null = item.location || this.employeeLocations[0]?.['id'] || null;
            (this.roleForm.get('list') as FormArray)?.push(
                this.fb.group({
                    level: defaultLevel,
                    type: defaultType,
                    location: defaultLocation,
                    count: item.count || 1,
                    isSelected: item.isSelected !== undefined ? item.isSelected : true,
                })
            );
        });
        this.roleForm.valueChanges.subscribe(() => {
            this.checkSelectedAll();
            this.checkEmployeeAmounts();
            this.formChange.emit(this.roleForm.getRawValue());
        });
        this.checkSelectedAll();
        this.checkEmployeeAmounts();
    }

    /**
     * Add a new item to the list with default values.
     */
    addNew(): void {
        const existList: FormArray = this.roleForm.get('list') as FormArray;
        const existLength: number = existList.length;
        existList?.push(
            this.fb.group({
                level: existLength ? null : this.employeeLevels[0]?.['id'] || null,
                type: existLength ? null : this.employeeTypes[0]?.['id'] || null,
                location: existLength ? null : this.employeeLocations[0]?.['id'] || null,
                count: 1,
                isSelected: true,
            })
        );
    }

    /**
     * Remove an item from the list.
     * @param {number} index The index of the item to remove.
     */
    removeItem(index: number): void {
        (this.roleForm.get('list') as FormArray)?.removeAt(index);
        this.checkSelectedAll();
    }

    /**
     * Select or deselect all items in the list.
     *
     * @param {MouseEvent} event The mouse event that triggered the selection.
     */
    selectAll(event: MouseEvent): void {
        event.stopPropagation();
        const target: HTMLElement = event.currentTarget as HTMLElement;
        let isChecked = null;
        if (target.tagName !== 'P-CHECKBOX') {
            isChecked = true;
            this.isSelectedAll = true;
        } else {
            isChecked = (event.target as HTMLInputElement).checked;
        }
        this.list.controls.forEach((control) => control.get('isSelected')?.setValue(isChecked));
    }

    /**
     * Check if all items in the list are selected.
     */
    checkSelectedAll(): void {
        const list = this.roleForm.get('list') as FormArray;
        if (list) {
            let hasSelectedItem = list.controls.some((control) => control.get('isSelected')?.value);
            const bl = list.controls.every((control) => control.get('isSelected')?.value);
            if (list.length > 0) {
                this.isSelectedAll = bl === false && hasSelectedItem ? undefined : bl;
            } else {
                this.isSelectedAll = false;
            }
        }
    }

    /**
     * Calculate the total number of selected employees.
     */
    checkEmployeeAmounts(): void {
        this.employeeAmonuts = this.list.controls.reduce((total, control) => {
            if (control.get('isSelected')?.value && this.checkRowIsValid(control)) {
                const count = control.get('count')?.value || 0;
                return total + count;
            }
            return total;
        }, 0);
    }

    /**
     * Get the name of the employee level by its ID.
     *
     * @param {number} levelId - The level id;
     * @returns {string} - The level name.
     */
    getLevelName(levelId: number): string {
        return this.employeeLevels.find((level: EmployeeLevel) => level.id === levelId)?.name || '';
    }

    /**
     * Check if the row is valid. The new add row will not be add to the form before fill the type, level, location.
     *
     * @param {AbstractControl<unknown, unknown>} row - The form group representing the row.
     * @returns {boolean} True if the row is valid, false otherwise.
     */
    checkRowIsValid(row: AbstractControl<unknown, unknown>): boolean {
        const level: number | null = row.get('level')?.value;
        const type: number | null = row.get('type')?.value;
        const location: number | null = row.get('location')?.value;
        return (level || level === 0) && (type || type === 0) && (location || location === 0) ? true : false;
    }

    /**
     * Check the current row is duplicate
     *
     * @param {number} index - The index of the current row.
     * @returns {boolean} True if the row is duplicate, false otherwise.
     */
    checkHasDuplicate(index: number): boolean {
        const list = this.roleForm.get('list') as FormArray;
        const currentItem = list?.at(index);
        if (currentItem && index > 0 && this.checkRowIsValid(currentItem)) {
            for (let i = 0; i < index; i++) {
                const item = list.at(i);
                if (item.get('level')?.value === currentItem.get('level')?.value && item.get('type')?.value === currentItem.get('type')?.value && item.get('location')?.value === currentItem.get('location')?.value) {
                    return true;
                }
            }
        }
        return false;
    }
}
